/**
 * Generate a single-page A4 PDF from a DOM node (the printable .tally-print-root).
 * Uses html2pdf.js (jsPDF + html2canvas under the hood).
 *
 * Returns a Promise<{ blob, filename }>.
 */
import html2pdf from "html2pdf.js";

export async function generateInvoicePdf(rootElement, filename = "invoice.pdf") {
  if (!rootElement) throw new Error("No element passed for PDF generation");

  const opt = {
    margin: [4, 4, 4, 4], // mm
    filename,
    image: { type: "jpeg", quality: 0.95 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"], avoid: ["tr", "img"] },
  };

  // Toggle off the Emergent badge during capture (defensive — also hidden via beforeprint listener)
  const badge = document.getElementById("emergent-badge");
  const prev = badge ? badge.style.display : null;
  if (badge) badge.style.display = "none";

  try {
    const blob = await html2pdf().set(opt).from(rootElement).outputPdf("blob");
    return { blob, filename };
  } finally {
    if (badge) badge.style.display = prev || "inline-flex";
  }
}

/**
 * Try to share a File via the native Web Share API (mobile WhatsApp / etc.).
 * Returns true if shared, false if Web Share-files isn't supported (caller should fall back).
 */
export async function shareFileNative({ blob, filename, title, text, url }) {
  if (typeof navigator === "undefined" || !navigator.canShare || !navigator.share) return false;
  const file = new File([blob], filename, { type: blob.type || "application/pdf" });
  const payload = { files: [file], title, text };
  if (url) payload.url = url;
  if (!navigator.canShare(payload)) return false;
  try {
    await navigator.share(payload);
    return true;
  } catch (e) {
    // User cancelled or browser threw — treat as "not shared"
    if (e?.name === "AbortError") return true; // user explicitly cancelled — don't fall back
    return false;
  }
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
}
